import { useMemo } from 'react';
import { useAuth } from '../contexts/ClerkAuthContext';

export interface ApiOptions extends RequestInit {
  requiresAuth?: boolean;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleErrorResponse(response: Response, requiresAuth: boolean): Promise<never> {
  let errorMessage = 'An error occurred';

  try {
    const errorData = await response.json();
    if (errorData.error) {
      errorMessage = errorData.error;
    } else if (errorData.message) {
      errorMessage = errorData.message;
    }
  } catch {
    // If we can't parse the error response, use the status text
    errorMessage = response.statusText || errorMessage;
  }

  if (response.status === 401 && requiresAuth) {
    // Dispatch a custom event that the auth context can listen for
    window.dispatchEvent(new Event('auth:unauthorized'));
    throw new ApiError(401, 'Your session has expired. Please sign in again.');
  }

  throw new ApiError(response.status, errorMessage);
}

export const useApi = () => {
  const { getToken } = useAuth();

  const api = useMemo(() => ({
    async fetch(endpoint: string, options: ApiOptions = {}) {
      const { requiresAuth = true, headers = {}, ...rest } = options;
      
      // Add auth header if required
      if (requiresAuth) {
        const token = await getToken();
        if (!token) {
          throw new ApiError(401, 'No authentication token found');
        }
        headers['Authorization' as keyof HeadersInit] = `Bearer ${token}`;
      }

      const response = await fetch(endpoint, {
        ...rest,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      if (!response.ok) {
        return handleErrorResponse(response, requiresAuth);
      }

      return response.json();
    },

    // Convenience methods
    get(endpoint: string, options: ApiOptions = {}) {
      return this.fetch(endpoint, { ...options, method: 'GET' });
    },

    post(endpoint: string, data: any, options: ApiOptions = {}) {
      return this.fetch(endpoint, { ...options, method: 'POST', body: JSON.stringify(data) });
    },

    delete(endpoint: string, options: ApiOptions = {}) {
      return this.fetch(endpoint, { ...options, method: 'DELETE' });
    },

    // Special method for Server-Sent Events
    async sse(endpoint: string, data: any, options: ApiOptions = {}) {
      const { requiresAuth = true, headers = {}, ...rest } = options;
      
      // Add auth header if required
      if (requiresAuth) {
        const token = await getToken();
        if (!token) {
          throw new ApiError(401, 'No authentication token found');
        }
        headers['Authorization' as keyof HeadersInit] = `Bearer ${token}`;
      }

      const response = await fetch(endpoint, {
        ...rest,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        return handleErrorResponse(response, requiresAuth);
      }

      return response;
    }
  }), [getToken]);

  return api;
};

 